import React, { useState } from 'react';

import { formatAmount, formatCount } from './uploadFormat';

import Alert from 'CommonComponents/Alert';
import Button from 'CommonComponents/Button';
import CheckboxInput from 'CommonComponents/CheckboxInput';

function Position({ title, position }) {
    return (
        <div className="tw-rounded-lg tw-border tw-border-charcoal-15 tw-px-4 tw-py-4">
            <div className="tw-text-xs tw-font-medium tw-uppercase tw-tracking-wide tw-text-charcoal-50">{title}</div>
            <div className="tw-mt-2 tw-text-xl tw-font-semibold tw-text-charcoal">{$t('{{count}} transactions', { count: formatCount(position.count) })}</div>
            <div className="tw-text-sm tw-text-charcoal-75">{$t('Net value {{value}}', { value: formatAmount(position.net_value) })}</div>
        </div>
    );
}

function ActionRow({ label, description, count, value }) {
    return (
        <tr>
            <td>
                <div className="tw-font-medium">{label}</div>
                <div className="tw-text-xs tw-text-charcoal-50">{description}</div>
            </td>
            <td className="tw-text-end">{formatCount(count)}</td>
            <td className="tw-text-end">{value === null ? '-' : formatAmount(value)}</td>
        </tr>
    );
}

/**
 * Step 3. The position before, what the upload does, and the position after,
 * with the matching rule spelled out. Upload stays disabled until the user
 * confirms they have read it.
 */
export default function UploadImpactReview({ batch, submitting, error, conflict, onBack, onCancel, onConfirm, onCheckAgain }) {
    const [acknowledged, setAcknowledged] = useState(false);
    const { impact, counts } = batch.summary;

    return (
        <div className="tw-flex tw-flex-col tw-gap-y-4">
            <section className="tw-rounded-lg tw-bg-white tw-shadow-primary tw-px-6 tw-py-6.5">
                <h3 className="table__header">{$t('What this upload will do')}</h3>
                <p className="tw-mt-1 tw-text-xs tw-text-charcoal-50">
                    {batch.file_name} · {batch.reference}
                </p>

                <div className="tw-mt-4 tw-rounded-lg tw-bg-blue-10 tw-px-4 tw-py-3 tw-text-sm tw-text-charcoal-85">
                    <span className="tw-font-semibold">{$t('Matching rule:')}</span>{' '}
                    {$t(
                        'each row is matched on its Unique Document Reference across the whole project. A new reference is inserted, a matched reference is updated in place, and nothing is ever deleted.',
                    )}
                </div>

                <div className="tw-mt-4 tw-grid tw-grid-cols-1 tw-gap-4 lg:tw-grid-cols-3">
                    <Position title={$t('Position before')} position={impact.before} />
                    <div className="inbud-table_wrapper">
                        <table className="inbud-table tw-w-full">
                            <thead>
                                <tr>
                                    <th>{$t('Action')}</th>
                                    <th className="tw-w-20 tw-text-end">{$t('Rows')}</th>
                                    <th className="tw-w-32 tw-text-end">{$t('Value')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                <ActionRow
                                    label={$t('Insert')}
                                    description={$t('New references')}
                                    count={impact.insert.count}
                                    value={impact.insert.net_value}
                                />
                                <ActionRow
                                    label={$t('Update')}
                                    description={$t('Matched references with changed values')}
                                    count={impact.update.count}
                                    value={impact.update.net_value}
                                />
                                <ActionRow
                                    label={$t('Leave untouched')}
                                    description={$t('Matched references with no change')}
                                    count={impact.unchanged.count}
                                    value={impact.unchanged.net_value}
                                />
                                <ActionRow label={$t('Delete')} description={$t('Uploads never delete')} count={0} value={null} />
                            </tbody>
                        </table>
                    </div>
                    <Position title={$t('Position after')} position={impact.after} />
                </div>

                {counts.blocked > 0 && (
                    <p className="tw-mt-4 tw-text-xs tw-text-charcoal-75">
                        {$t('{{count}} blocked rows are held back and are not part of these figures.', { count: formatCount(counts.blocked) })}
                    </p>
                )}
            </section>

            {!!error && <Alert type="error">{error}</Alert>}

            {!!conflict && (
                <Alert type="warning">
                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                        <span>{conflict}</span>
                        <Button small variant="primary" onClick={onCheckAgain}>
                            {$t('Check the file again')}
                        </Button>
                    </div>
                </Alert>
            )}

            <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3">
                <CheckboxInput
                    checked={acknowledged && !conflict}
                    disabled={!!conflict}
                    onChange={(event) => setAcknowledged(event.target.checked)}
                    label={$t('I have reviewed the impact and want to write {{count}} rows', { count: formatCount(counts.loadable) })}
                />
                <div className="tw-flex tw-items-center tw-gap-2">
                    <Button variant="secondary" onClick={onBack} disabled={submitting}>
                        {$t('Back')}
                    </Button>
                    <Button variant="secondary" onClick={onCancel} disabled={submitting}>
                        {$t('Cancel')}
                    </Button>
                    <Button variant="primary" onClick={onConfirm} disabled={!acknowledged || submitting || !!conflict} loading={submitting}>
                        {$t('Upload')}
                    </Button>
                </div>
            </div>
        </div>
    );
}
